<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { computed, ref } from "vue";
import { useFormModel } from "@/hooks";
import {
  errorLog,
  error,
  warning,
  getUuid,
  compressImage,
  getToken,
} from "@/utils";
import { useStore } from "@/stores";
import type { ImageFit } from "@nutui/nutui";

const props = withDefaults(
  defineProps<{
    url?: string;
    height?: string;
    width?: string;
    radius?: string;
    limit?: number;
    maximize: number;
    fit?: ImageFit;
    preview: boolean;
    modelValue?: string;
  }>(),
  { url: "File/Upload" }
);

const emit = defineEmits(["update:modelValue", "change"]);

const uploadRef = ref();
const token = getToken();
const headers: Record<string, any> = { uuid: getUuid(), token };
const showPreview = ref(false);
const imageIndex = ref(0);

const apiUpload = computed(
  () => `/api/${props.url}`
);
const uploadMaximize = computed(() => props.maximize * 1024);

const imgStyle = computed(() => ({
  height: props.height,
  width: props.width,
  borderRadius: props.radius,
}));

const { uploadDirectory } = useStore();

const fileList = computed<string[]>(() => {
  if (!props.modelValue) return [];
  return props.modelValue
    .split(",")
    .map((image) => `${uploadDirectory}/${image}`);
});

const previewList = computed(() =>
  fileList.value.map((file) => ({ src: file }))
);

function onClickItem(inx: number) {
  if (!props.preview) return;
  imageIndex.value = inx;
  showPreview.value = true;
}

function onDelete(inx: number) {
  const arr = props.modelValue!.split(",");
  arr.splice(inx, 1);
  const newValue = arr.length > 0 ? arr.join(",") : null;
  emit("update:modelValue", newValue);
  emit("change", newValue);
  // if (props.validateEvent) {
  //   elFormItem?.validate?.("change").catch((err) => console.log(err));
  // }
}

function onSuccess({ responseText }: { responseText: string }) {
  uploadRef.value.clearUploadQueue();
  const result = JSON.parse(responseText);
  if (!result.success) return;
  const newValue = props.modelValue
    ? `${props.modelValue},${result.data[0].fileName}`
    : result.data[0].fileName;
  emit("update:modelValue", newValue);
  emit("change", newValue);
}

function onFailure({ responseText }: { responseText: string }) {
  errorLog(responseText);
  error("图片上传出错了，请联系管理员");
}

function onOversize() {
  warning(`文件大小不能超过${props.maximize}KB`);
}

const beforeUpload = async (files: any[]) => {
  if (
    props.limit !== undefined &&
    props.limit > 0 &&
    fileList.value.length === props.limit
  )
    return false;

  if (files.length === 0) return [];
  const file = await compressImage(files[0], 0.5);
  return [file];
};

const { setFormModel } = useFormModel();
defineExpose({ setFormModel });
</script>
<template>
  <div class="mp-image-multi-uploader">
    <div
      class="mp-image-multi-uploader__item"
      v-for="(file, index) in fileList"
      :key="index"
      @click="onClickItem(index)"
    >
      <nut-image
        class="mp-image-multi-uploader__item-image"
        :src="file"
        :fit="fit"
        :width="width"
        :height="height"
        :radius="radius"
      />
      <div class="mp-image-multi-uploader__item-actions">
        <span @click.stop="onDelete(index)">
          <WxIcon name="ant-design-close-circle-twotone" />
        </span>
      </div>
    </div>
    <nut-uploader
      class="mp-image-multi-uploader__uploader"
      v-bind="$attrs"
      :style="imgStyle"
      :url="apiUpload"
      :headers="headers"
      :auto-upload="true"
      :multiple="false"
      :maximize="uploadMaximize"
      :before-upload="beforeUpload"
      ref="uploadRef"
      @success="onSuccess"
      @failure="onFailure"
      @oversize="onOversize"
    >
    </nut-uploader>
  </div>
  <nut-image-preview
    :show="showPreview"
    :images="previewList"
    :init-no="imageIndex"
    :autoplay="0"
    @close="showPreview = false"
  />
</template>
<style lang="scss">
.mp-image-multi-uploader {
  display: inline-flex;
  flex-wrap: wrap;

  &__item {
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    margin: 0 5px 5px 0;

    &-actions {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
      font-size: 20px;
    }
  }
}
</style>
